import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState } from 'react';
import { useDishonorCheck, type CheckTransaction } from '@/hooks/queries/useChecks';
import { toast } from '@/components/ui/toast/useToast';
import { XCircle } from 'lucide-react';

interface DishonorCheckModalProps {
  open: boolean;
  onClose: () => void;
  check: CheckTransaction | null;
}

const BOUNCE_REASONS = [
  { value: 'insufficient_funds', label: 'Provision insuffisante' },
  { value: 'account_closed', label: 'Compte clôturé' },
  { value: 'signature_mismatch', label: 'Signature non conforme' },
  { value: 'stopped_payment', label: 'Opposition sur le chèque' },
  { value: 'expired', label: 'Chèque expiré (>180 jours)' },
  { value: 'technical_error', label: 'Erreur technique' },
  { value: 'other', label: 'Autre raison' },
];

export function DishonorCheckModal({ open, onClose, check }: DishonorCheckModalProps) {
  const [bounceReason, setBounceReason] = useState('');
  const [bounceFee, setBounceFee] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dishonorCheck = useDishonorCheck();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!check || !bounceReason) return;

    setIsSubmitting(true);

    try {
      await dishonorCheck.mutateAsync({
        checkId: check.id,
        bounceReason: BOUNCE_REASONS.find((r) => r.value === bounceReason)?.label || bounceReason,
        bounceFee: bounceFee ? parseFloat(bounceFee) : undefined,
        notes: notes || undefined,
      });

      toast.success(`Le chèque ${check.check_number} a été marqué comme rejeté.`);

      setBounceReason('');
      setBounceFee('');
      setNotes('');
      onClose();
    } catch (error) {
      console.error('Error dishonoring check:', error);
      toast.error('Impossible de marquer le chèque comme rejeté. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!check) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <XCircle className="w-6 h-6" />
            Marquer comme rejeté
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">N° Chèque:</span>
              <span className="font-mono font-semibold">{check.check_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Tireur:</span>
              <span className="font-medium">{check.check_drawer_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Montant:</span>
              <span className="text-lg font-bold text-red-600">
                {formatCurrency(check.amount)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bounceReason">Motif de rejet *</Label>
            <Select value={bounceReason} onValueChange={setBounceReason} required>
              <SelectTrigger id="bounceReason">
                <SelectValue placeholder="Sélectionnez le motif" />
              </SelectTrigger>
              <SelectContent>
                {BOUNCE_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bounceFee">Frais de rejet (DA)</Label>
            <Input
              id="bounceFee"
              type="number"
              value={bounceFee}
              onChange={(e) => setBounceFee(e.target.value)}
              placeholder="Ex: 2000"
              min="0"
              step="100"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes complémentaires</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Notification reçue le 15/12, client contacté..."
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="destructive"
              className="flex-1"
              disabled={isSubmitting || !bounceReason}
            >
              {isSubmitting ? 'Traitement...' : '❌ Marquer comme rejeté'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
